import { CSmartTable } from '@coreui/react-pro';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { formatDate } from '../../../../../utils';
import { DEFAULT_DATE_MONTH_NAME_TIME_FORMAT, DEFAULT_PAGINATION, SORTING_TYPE, TRAINING_PRODUCT_TYPES } from '../../../../../constants';

import Pagination from '../../../../General/Pagination';
import PerPageOption from '../../../../General/Pagination/PerPageOption';

import { getOrganizationTrainingReports } from '../actions';

const ResultReports = ({ reportPreview, setReportPreview, pagination, setPagination }) => {
     const dispatch = useDispatch();

     const REPORT_COLUMNS = [
          {
               key: 'organizationName',
               label: 'Organization',
               _style: { minWidth: '150px', width: '300px' },
          },
          {
               key: 'productName',
               label: 'Detail Product Name',
               _style: { minWidth: '150px', width: '300px' },
          },
          {
               key: 'seatCount',
               label: 'Total Number of Seats Delivered',
               _style: { minWidth: '150px' },
               sorter: false,
          },
          {
               key: 'reservedSeats',
               label: 'Number of Seats Assigned',
               _style: { minWidth: '150px' },
               sorter: false,
          },
          {
               key: 'filledSeats',
               label: 'Number of Seats Accepted',
               _style: { minWidth: '150px' },
               sorter: false,
          },
          {
               key: 'eventStartAt',
               label: 'Date of Live Training',
               _style: { minWidth: '150px' },
          },
          {
               key: 'numberOfConfirmedAttendees',
               label: 'Number of Confirmed Attendees',
               _style: { minWidth: '150px' },
               sorter: false,
          },
     ];

     const handleChangePage = useCallback(
          (e) => {
               const page = e.selected + 1;

               setPagination((currentPagination) => ({
                    ...currentPagination,
                    page,
               }));

               const paginationPayload = {
                    ...pagination,
                    page,
               };

               const nextDispatch = {
                    paginationPayload,
                    setReportPreview,
               };
               dispatch(getOrganizationTrainingReports(nextDispatch));
          },
          [pagination] // eslint-disable-line react-hooks/exhaustive-deps
     );

     const handleChangeCustomPage = useCallback(
          (page) => {
               setPagination((currentPagination) => ({
                    ...currentPagination,
                    page,
               }));

               const paginationPayload = {
                    ...pagination,
                    page,
               };

               const nextDispatch = {
                    paginationPayload,
                    setReportPreview,
               };
               dispatch(getOrganizationTrainingReports(nextDispatch));
          },
          [pagination] // eslint-disable-line react-hooks/exhaustive-deps
     );

     const handleChangeItemsPerPage = (pageSize) => {
          if (pagination.pageSize === pageSize) return;

          setPagination((currentPagination) => ({
               ...currentPagination,
               page: DEFAULT_PAGINATION.PAGE,
               pageSize,
          }));

          const paginationPayload = {
               ...pagination,
               page: DEFAULT_PAGINATION.PAGE,
               pageSize,
          };

          const nextDispatch = {
               paginationPayload,
               setReportPreview,
          };
          dispatch(getOrganizationTrainingReports(nextDispatch));
     };

     const handleChangeSort = useCallback(
          (e) => {
               let sortBy = e.column;
               let sortType = e.state === SORTING_TYPE.DESC ? SORTING_TYPE.DESC_NULL_LAST : SORTING_TYPE.ASC;

               if (sortType === 0) {
                    sortBy = 'organizationName';
                    sortType = SORTING_TYPE.DESC_NULL_LAST;
               }

               if (!Object.keys(e).length || (pagination.sortBy === sortBy && pagination.sortType === sortType)) {
                    return;
               }

               setPagination((currentPagination) => ({
                    ...currentPagination,
                    sortBy,
                    sortType,
               }));

               const paginationPayload = {
                    ...pagination,
                    sortBy,
                    sortType,
               };

               const nextDispatch = {
                    paginationPayload,

                    setReportPreview,
               };
               dispatch(getOrganizationTrainingReports(nextDispatch));
          },
          [pagination] // eslint-disable-line react-hooks/exhaustive-deps
     );

     return (
          <div className="organization-training-reports">
               <p className="mb-0">
                    Total: <strong>{reportPreview.countParent.toLocaleString('en-US')}</strong>
               </p>
               <div className="middle-table">
                    <CSmartTable
                         clickableRows
                         tableProps={{
                              striped: true,
                              responsive: true,
                         }}
                         items={reportPreview?.rows}
                         columns={REPORT_COLUMNS}
                         itemsPerPage={DEFAULT_PAGINATION.PAGE_SIZE}
                         onSorterChange={handleChangeSort}
                         columnSorter={{ external: true, resetable: true }}
                         scopedColumns={{
                              seatCount: (item) => {
                                   return <td className="text-center">{item.seatCount ?? ''}</td>;
                              },
                              reservedSeats: (item) => {
                                   return <td className="text-center">{item.reservedSeats ?? ''}</td>;
                              },
                              filledSeats: (item) => {
                                   return <td className="text-center">{item.filledSeats ?? ''}</td>;
                              },
                              eventStartAt: (item) => {
                                   const isEventScheduled = item.eventId && item.eventStartAt;
                                   const isLiveTraining = item.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;

                                   const result = isEventScheduled
                                        ? formatDate({
                                               date: item.eventStartAt,
                                               timezone: item.eventTimeZone,
                                               format: DEFAULT_DATE_MONTH_NAME_TIME_FORMAT,
                                          })
                                        : isLiveTraining
                                        ? 'Not Scheduled Yet'
                                        : '';

                                   return <td>{result}</td>;
                              },
                              numberOfConfirmedAttendees: (item) => {
                                   return <td className="text-center">{item.numberOfConfirmedAttendees ?? ''}</td>;
                              },
                         }}
                    />
                    <div className="row">
                         <div className="col">
                              <Pagination
                                   currentPage={pagination.page}
                                   pageSize={pagination.pageSize}
                                   pageCount={reportPreview?.countParent}
                                   onChangePage={handleChangePage}
                                   onChangeCustomPage={handleChangeCustomPage}
                              />
                         </div>
                         <div className="col-auto ms-auto">
                              <PerPageOption label="Items per page:" value={pagination.pageSize} onChange={handleChangeItemsPerPage} />
                         </div>
                    </div>
               </div>
          </div>
     );
};

ResultReports.propTypes = {
     reportPreview: PropTypes.object,
     setReportPreview: PropTypes.func,
     pagination: PropTypes.object,
     setPagination: PropTypes.func,
};
export default ResultReports;
